import React from 'react';
import { cn } from '@/lib/utils';

/**
 * CoRegulate Progress Components
 * 
 * Gentle visual indicators for progress
 * Colors: Sage Green (#7D8E95), Soft Sand (#FFBB98)
 * Smooth, slow animations
 */

/**
 * Progress Bar
 * Horizontal progress indicator
 */
export const ProgressBar = ({
  value = 0, // 0-100
  max = 100,
  className,
  size = 'md', // 'sm' | 'md' | 'lg'
  variant = 'sage', // 'sage' | 'eucalyptus' | 'lavender' | 'blue'
  showLabel = false,
  label,
  ...props
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const sizeStyles = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  };

  const variantStyles = {
    sage: 'bg-[#7D8E95]',
    eucalyptus: 'bg-[#7D8E95]',
    lavender: 'bg-[#7D8E95]',
    blue: 'bg-[#7D8E95]',
    rose: 'bg-[#FFBB98]',
  };

  return (
    <div className={cn('w-full', className)} {...props}>
      {(showLabel || label) && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-[#2A8585]">
            {label}
          </span>
          {showLabel && (
            <span className="text-sm font-medium text-[#1D6B6B]">
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}
      <div
        className={cn(
          'w-full rounded-full overflow-hidden',
          'bg-[#FFBB98]',
          sizeStyles[size]
        )}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div
          className={cn(
            'h-full rounded-full',
            'transition-all duration-600 ease-out',
            variantStyles[variant]
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
ProgressBar.displayName = 'ProgressBar';

/**
 * Progress Dots
 * Dot-based step indicator
 */
export const ProgressDots = ({
  total = 5,
  current = 0,
  className,
  size = 'md', // 'sm' | 'md' | 'lg'
  variant = 'sage',
  onClick,
  ...props
}) => {
  const sizeStyles = {
    sm: { dot: 'w-2 h-2', active: 'w-3 h-3', gap: 'gap-2' },
    md: { dot: 'w-2.5 h-2.5', active: 'w-3.5 h-3.5', gap: 'gap-3' },
    lg: { dot: 'w-3 h-3', active: 'w-4 h-4', gap: 'gap-4' },
  };

  const variantStyles = {
    sage: 'bg-[#7D8E95]',
    eucalyptus: 'bg-[#7D8E95]',
    lavender: 'bg-[#7D8E95]',
  };

  const s = sizeStyles[size];

  return (
    <div
      className={cn('flex items-center justify-center', s.gap, className)}
      role="tablist"
      aria-label="Progress"
      {...props}
    >
      {Array.from({ length: total }).map((_, index) => {
        const isActive = index === current;
        const isCompleted = index < current;

        return (
          <button
            key={index}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`Step ${index + 1}`}
            onClick={() => onClick?.(index)}
            disabled={!onClick}
            className={cn(
              'rounded-full',
              'transition-all duration-500 ease-out',
              isActive ? s.active : s.dot,
              isActive
                ? variantStyles[variant]
                : isCompleted
                  ? `${variantStyles[variant]} opacity-50`
                  : 'bg-[#FFBB98]',
              onClick && 'cursor-pointer hover:scale-110',
              !onClick && 'cursor-default'
            )}
          />
        );
      })}
    </div>
  );
};
ProgressDots.displayName = 'ProgressDots';

/**
 * Step Indicator
 * Numbered step indicator with labels
 */
export const StepIndicator = ({
  steps = [],
  current = 0,
  className,
  orientation = 'horizontal', // 'horizontal' | 'vertical'
  ...props
}) => {
  if (orientation === 'vertical') {
    return (
      <div className={cn('flex flex-col', className)} {...props}>
        {steps.map((step, index) => {
          const isActive = index === current;
          const isCompleted = index < current;

          return (
            <div key={index} className="flex gap-4">
              {/* Step marker */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full',
                    'flex items-center justify-center',
                    'text-sm font-medium',
                    'transition-all duration-400',
                    isActive
                      ? 'bg-[#7D8E95] text-[#1D6B6B]'
                      : isCompleted
                        ? 'bg-[#7D8E95]/50 text-[#1D6B6B]'
                        : 'bg-[#FFBB98] text-[#2A8585]'
                  )}
                >
                  {isCompleted ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8L6 11L13 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'w-0.5 h-12',
                      'transition-colors duration-400',
                      isCompleted ? 'bg-[#7D8E95]/50' : 'bg-[#FFBB98]'
                    )}
                  />
                )}
              </div>

              {/* Step content */}
              <div className="pt-1 pb-8">
                <p className={cn(
                  'font-medium text-sm',
                  isActive ? 'text-[#1D6B6B]' : 'text-[#2A8585]'
                )}>
                  {step.label}
                </p>
                {step.description && (
                  <p className="text-sm text-[#7D8E95] mt-1">
                    {step.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Horizontal orientation
  return (
    <div className={cn('w-full', className)} {...props}>
      <div className="flex items-center">
        {steps.map((step, index) => {
          const isActive = index === current;
          const isCompleted = index < current;

          return (
            <React.Fragment key={index}>
              {/* Step */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'w-10 h-10 rounded-full',
                    'flex items-center justify-center',
                    'text-sm font-medium',
                    'transition-all duration-400',
                    isActive
                      ? 'bg-[#7D8E95] text-[#1D6B6B] shadow-sm'
                      : isCompleted
                        ? 'bg-[#7D8E95]/50 text-[#1D6B6B]'
                        : 'bg-[#FFBB98] text-[#2A8585]'
                  )}
                >
                  {isCompleted ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M4 9L7 12L14 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span className={cn(
                  'mt-2 text-xs font-medium text-center max-w-[80px]',
                  isActive ? 'text-[#1D6B6B]' : 'text-[#2A8585]'
                )}>
                  {step.label}
                </span>
              </div>

              {/* Connector */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2 mt-[-20px]',
                    'transition-colors duration-400',
                    isCompleted ? 'bg-[#7D8E95]/50' : 'bg-[#FFBB98]'
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
StepIndicator.displayName = 'StepIndicator';

/**
 * Circular Progress
 * Ring-style progress indicator
 */
export const CircularProgress = ({
  value = 0,
  max = 100,
  size = 80,
  strokeWidth = 6,
  className,
  variant = 'sage',
  showValue = true,
  label,
  ...props
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const variantStyles = {
    sage: 'stroke-[#7D8E95]',
    eucalyptus: 'stroke-[#7D8E95]',
    lavender: 'stroke-[#7D8E95]',
    rose: 'stroke-[#FFBB98]',
  };

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      {...props}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#FFBB98"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          className={cn(
            variantStyles[variant],
            'transition-all duration-600 ease-out'
          )}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      {(showValue || label) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {showValue && (
            <span className="text-lg font-semibold text-[#1D6B6B]">
              {Math.round(percentage)}%
            </span>
          )}
          {label && (
            <span className="text-xs text-[#2A8585]">
              {label}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
CircularProgress.displayName = 'CircularProgress';

export default ProgressBar;
